<template>
  <el-dialog title="时效质检规则" :visible.sync="dialogVisible" width="1120px" :close-on-click-modal="false">
    <div v-loading="loading" class="timeliness-dialog-body">
      <div class="status-row">
        <div>
          <strong>规则状态</strong>
          <div class="sub-text">开启后按工作时段扫描会话，临近超时或超时命中会触发企微提醒。</div>
        </div>
        <el-switch v-model="status" :active-value="1" :inactive-value="0" active-text="开启" inactive-text="关闭"></el-switch>
      </div>

      <div class="rule-filter">
        <span>主游戏筛选</span>
        <el-select v-model="filterGameKeys" multiple filterable clearable collapse-tags placeholder="全部主游戏" style="width: 420px">
          <el-option v-for="game in mainGameOptions" :key="game.appKey" :label="game.text" :value="game.appKey"></el-option>
        </el-select>
        <span class="sub-text">共 {{ filteredRules.length }} 条规则</span>
      </div>

      <div class="rule-layout">
        <aside class="rule-sidebar">
          <div class="section-head">
            <strong>规则维度</strong>
            <el-button type="text" icon="el-icon-plus" @click="addRule">新增</el-button>
          </div>
          <div class="rule-list">
            <button
              v-for="entry in filteredRules"
              :key="entry.rule._key"
              type="button"
              class="rule-item"
              :class="{ active: entry.index === activeRuleIndex }"
              @click="selectRule(entry.index)"
            >
              <span class="rule-item-text" :title="ruleTitle(entry.rule)">{{ ruleTitle(entry.rule) }}</span>
              <i class="el-icon-delete" title="删除规则维度" @click.stop="removeRule(entry.index)"></i>
            </button>
            <div v-if="!filteredRules.length" class="empty-rules">暂无匹配规则</div>
          </div>
        </aside>

        <section v-if="activeRule" class="rule-content">
          <div class="dimension-row">
            <el-form label-width="92px" inline>
              <el-form-item label="规则维度">
                <el-select v-model="activeRule.dimension" style="width: 180px" @change="handleDimensionChange">
                  <el-option label="通用" value="common" :disabled="commonOptionDisabled"></el-option>
                  <el-option label="按主游戏" value="game"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item v-if="activeRule.dimension === 'game'" label="主游戏">
                <el-select
                  v-model="activeRule.main_game_app_keys"
                  multiple
                  filterable
                  collapse-tags
                  placeholder="请选择主游戏"
                  style="width: 360px"
                >
                  <el-option
                    v-for="game in mainGameOptions"
                    :key="game.appKey"
                    :label="game.text"
                    :value="game.appKey"
                    :disabled="isGameUsed(game.appKey)"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-form>
          </div>

          <div class="tier-head">
            <div class="tier-selector">
              <strong>充值层级</strong>
              <el-select v-model="activeTierIndex" style="width: 260px">
                <el-option
                  v-for="(tier, index) in activeRule.tiers"
                  :key="tier._key"
                  :label="tierTitle(tier)"
                  :value="index"
                ></el-option>
              </el-select>
            </div>
            <div>
              <el-button type="text" icon="el-icon-plus" @click="addTier">新增层级</el-button>
              <el-button type="text" icon="el-icon-delete" @click="removeTier">删除层级</el-button>
            </div>
          </div>

          <div v-if="activeTier" class="tier-form">
            <el-form label-width="100px">
              <el-form-item label="层级名称">
                <el-input v-model.trim="activeTier.tier_name" maxlength="10" style="width: 280px"></el-input>
              </el-form-item>
              <el-form-item label="充值区间">
                <div class="range-inputs">
                  <el-input v-model.trim="activeTier.min_recharge" placeholder="最低值">
                    <template slot="prepend">¥</template>
                  </el-input>
                  <span>≤ 充值金额 &lt;</span>
                  <el-input v-model.trim="activeTier.max_recharge" placeholder="最高值留空表示无上限">
                    <template slot="prepend">¥</template>
                  </el-input>
                </div>
              </el-form-item>
            </el-form>

            <el-table :data="activeTier.thresholds" border size="small" class="threshold-table">
              <el-table-column label="单双号" width="80">
                <template slot-scope="scope">{{ scope.row.account_type === 'double' ? '双号' : '单号' }}</template>
              </el-table-column>
              <el-table-column prop="period_label" label="固定工作时段" width="170"></el-table-column>
              <el-table-column label="临近超时（分钟）">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.near_timeout_minutes" :min="1" :max="1440" :step="1" :precision="0" controls-position="right"></el-input-number>
                </template>
              </el-table-column>
              <el-table-column label="超时（分钟）">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.timeout_minutes" :min="1" :max="1440" :step="1" :precision="0" controls-position="right"></el-input-number>
                </template>
              </el-table-column>
              <el-table-column label="严重超时（分钟）">
                <template slot-scope="scope">
                  <el-input-number v-model="scope.row.serious_timeout_minutes" :min="1" :max="1440" :step="1" :precision="0" controls-position="right"></el-input-number>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="rule-hint">
            <div>充值金额口径为玩家最近充值角色所属主游戏的累计充值；无充值记录按 0 使用通用规则。</div>
            <div>充值层级必须从 0 开始、连续且无重叠，最后一层最高值留空。</div>
            <div>每个时段必须满足：临近超时 &lt; 超时 &lt; 严重超时；阈值范围为 1–1440 分钟。</div>
            <div>所选主游戏共用当前充值层级和时效阈值；通用规则最多一条，同一主游戏只能归属一条规则。</div>
            <div>同一客服当天负责的企微跨企业合计，超过一个为双号；单双号分别配置，未匹配排班按单号。</div>
            <div>跨日从当天10:00重新计时，使用当天排班及上午阈值；超时最多追溯前一天，漏回只检查当天20:00前未回复的有效消息。</div>
          </div>
        </section>
        <section v-else class="rule-content empty-rules">暂无匹配规则，请调整或清空主游戏筛选条件。</section>
      </div>
    </div>
    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" :disabled="loading || !configLoaded" @click="save">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  getQwAiQualityMainGameOptionsApi,
  getQwAiQualityTimelinessRuleApi,
  saveQwAiQualityTimelinessRuleApi,
} from '@/api/qwAiQuality';

const PERIODS = [
  { period_code: 'morning', period_no: 1, period_label: '10:00-12:00', start_time: '10:00:00', end_time: '12:00:00' },
  { period_code: 'noon', period_no: 2, period_label: '12:30-14:00', start_time: '12:30:00', end_time: '14:00:00' },
  { period_code: 'afternoon', period_no: 3, period_label: '14:30-18:00', start_time: '14:30:00', end_time: '18:00:00' },
  { period_code: 'evening', period_no: 4, period_label: '19:00-20:00', start_time: '19:00:00', end_time: '20:00:00' },
];
const ACCOUNT_TYPES = ['single', 'double'];

export default {
  data() {
    return {
      dialogVisible: false,
      loading: false,
      configLoaded: false,
      saving: false,
      status: 0,
      rules: [],
      mainGameOptions: [],
      filterGameKeys: [],
      activeRuleIndex: 0,
      activeTierIndex: 0,
      localKey: 1,
    };
  },
  computed: {
    filteredRules() {
      const selectedKeys = this.filterGameKeys.map(key => key.toLowerCase());
      return this.rules.map((rule, index) => ({ rule, index })).filter(entry => !selectedKeys.length ||
        (entry.rule.dimension === 'game' && entry.rule.main_game_app_keys.some(key => selectedKeys.includes(key.toLowerCase()))));
    },
    activeRule() {
      return this.rules[this.activeRuleIndex] || null;
    },
    activeTier() {
      if (!this.activeRule) return null;
      return this.activeRule.tiers[this.activeTierIndex] || null;
    },
    commonOptionDisabled() {
      return this.rules.some((rule, index) => index !== this.activeRuleIndex && rule.dimension === 'common');
    },
  },
  watch: {
    filteredRules() {
      this.syncActiveRule();
    },
  },
  methods: {
    async open() {
      this.dialogVisible = true;
      this.loading = true;
      this.configLoaded = false;
      this.filterGameKeys = [];
      this.rules = [];
      try {
        const responses = await Promise.all([
          getQwAiQualityTimelinessRuleApi(),
          getQwAiQualityMainGameOptionsApi().catch(() => null),
        ]);
        const ruleResponse = responses[0];
        const gameResponse = responses[1];
        if (!ruleResponse || ruleResponse.code !== 200) {
          this.$message.error((ruleResponse && ruleResponse.message) || '获取时效质检规则失败');
          return;
        }
        const gameOptionsLoaded = gameResponse && gameResponse.code === 200;
        this.mainGameOptions = (gameOptionsLoaded ? gameResponse.data || [] : []).map(item => ({
          id: item.id,
          appKey: String(item.appKey),
          text: item.text,
        }));
        this.applyConfig(ruleResponse.data || {});
        this.configLoaded = true;
        if (!gameOptionsLoaded) {
          this.$message.warning('主游戏选项加载失败，已保留现有规则，可继续编辑；新增主游戏请稍后重试');
        }
      } catch (error) {
        this.$message.error('获取时效质检规则失败');
      } finally {
        this.loading = false;
      }
    },
    applyConfig(config) {
      this.status = Number(config.status) === 1 ? 1 : 0;
      this.rules = (config.rules || []).map(rule => {
        const games = rule.dimension === 'game' ? (Array.isArray(rule.main_games) ? rule.main_games : (rule.main_game_app_key ? [rule] : [])) : [];
        games.forEach(game => {
          const appKey = String(game.main_game_app_key);
          if (!this.mainGameOptions.some(option => option.appKey === appKey)) {
            this.mainGameOptions.push({ id: game.main_game_id, appKey, text: game.main_game_name || appKey });
          }
        });
        return {
          _key: this.nextKey(),
          dimension: rule.dimension || 'common',
          main_game_app_keys: games.map(game => String(game.main_game_app_key)),
          tiers: (rule.tiers || []).map(tier => this.normalizeTier(tier)),
        };
      });
      this.activeRuleIndex = 0;
      this.activeTierIndex = 0;
    },
    normalizeTier(tier) {
      const thresholdMap = {};
      (tier.thresholds || []).forEach(item => {
        thresholdMap[(item.account_type || 'single') + ':' + item.period_code] = item;
      });
      return {
        _key: this.nextKey(),
        tier_name: tier.tier_name || '',
        min_recharge: tier.min_recharge === undefined || tier.min_recharge === null ? '' : String(tier.min_recharge),
        max_recharge: tier.max_recharge === undefined || tier.max_recharge === null ? '' : String(tier.max_recharge),
        thresholds: [].concat(...ACCOUNT_TYPES.map(accountType => PERIODS.map(period => {
          const saved = thresholdMap[accountType + ':' + period.period_code] || thresholdMap['single:' + period.period_code] || {};
          return Object.assign({}, period, {
            account_type: accountType,
            near_timeout_minutes: Number(saved.near_timeout_minutes || 45),
            timeout_minutes: Number(saved.timeout_minutes || 60),
            serious_timeout_minutes: Number(saved.serious_timeout_minutes || 120),
          });
        }))),
      };
    },
    nextKey() {
      const key = this.localKey;
      this.localKey += 1;
      return key;
    },
    newTier(name) {
      return this.normalizeTier({ tier_name: name, min_recharge: '', max_recharge: '', thresholds: [] });
    },
    addRule() {
      this.filterGameKeys = [];
      this.rules.push({
        _key: this.nextKey(),
        dimension: 'game',
        main_game_app_keys: [],
        tiers: [this.newTier('充值层级1')],
      });
      this.activeRuleIndex = this.rules.length - 1;
      this.activeTierIndex = 0;
    },
    selectRule(index) {
      this.activeRuleIndex = index;
      this.activeTierIndex = 0;
    },
    syncActiveRule() {
      if (!this.filteredRules.some(entry => entry.index === this.activeRuleIndex)) {
        this.selectRule(this.filteredRules.length ? this.filteredRules[0].index : -1);
      }
    },
    removeRule(index) {
      if (this.rules.length <= 1) {
        this.$message.warning('至少保留一个规则维度');
        return;
      }
      const title = this.ruleTitle(this.rules[index]);
      this.$confirm('确认删除“' + title + '”及其全部充值层级吗？', '删除规则维度', { type: 'warning' })
        .then(() => {
          const activeKey = this.activeRule ? this.activeRule._key : null;
          this.rules.splice(index, 1);
          this.activeRuleIndex = this.rules.findIndex(rule => rule._key === activeKey);
          this.syncActiveRule();
          this.activeTierIndex = 0;
        })
        .catch(() => {});
    },
    handleDimensionChange(dimension) {
      if (dimension === 'common') {
        this.activeRule.main_game_app_keys = [];
      }
    },
    isGameUsed(appKey) {
      return this.rules.some((rule, index) => index !== this.activeRuleIndex && rule.dimension === 'game' &&
        rule.main_game_app_keys.some(key => key.toLowerCase() === appKey.toLowerCase()));
    },
    addTier() {
      const nextNumber = this.activeRule.tiers.length + 1;
      this.activeRule.tiers.push(this.newTier('充值层级' + nextNumber));
      this.activeTierIndex = this.activeRule.tiers.length - 1;
    },
    removeTier() {
      if (this.activeRule.tiers.length <= 1) {
        this.$message.warning('当前规则维度至少保留一个充值层级');
        return;
      }
      this.$confirm('确认删除当前充值层级吗？', '删除充值层级', { type: 'warning' })
        .then(() => {
          this.activeRule.tiers.splice(this.activeTierIndex, 1);
          this.activeTierIndex = Math.min(this.activeTierIndex, this.activeRule.tiers.length - 1);
        })
        .catch(() => {});
    },
    ruleTitle(rule) {
      if (rule.dimension === 'common') return '通用规则';
      return rule.main_game_app_keys.map(key => {
        const game = this.mainGameOptions.find(item => item.appKey === key);
        return game ? game.text : key;
      }).join('、') || '未选择主游戏';
    },
    tierTitle(tier) {
      if (tier.min_recharge === '') return tier.tier_name || '未命名层级';
      const range = tier.max_recharge === '' ? tier.min_recharge + ' 以上' : tier.min_recharge + ' - ' + tier.max_recharge;
      return (tier.tier_name || '未命名层级') + '（' + range + '）';
    },
    amountToCents(value) {
      const text = String(value === undefined || value === null ? '' : value).trim();
      if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
      const parts = text.split('.');
      if (parts[0].length > 13) return null;
      return Number(parts[0]) * 100 + Number(((parts[1] || '') + '00').slice(0, 2));
    },
    validateConfig() {
      if (!this.rules.length) return '至少保留一个规则维度';
      if (this.rules.filter(rule => rule.dimension === 'common').length > 1) return '通用规则最多只能配置一条';
      const gameRules = this.rules.filter(rule => rule.dimension === 'game');
      if (gameRules.some(rule => !rule.main_game_app_keys.length)) return '主游戏规则必须选择主游戏';
      const gameKeys = [].concat(...gameRules.map(rule => rule.main_game_app_keys)).map(key => key.toLowerCase());
      if (new Set(gameKeys).size !== gameKeys.length) return '同一主游戏最多只能配置一条规则';
      for (let ruleIndex = 0; ruleIndex < this.rules.length; ruleIndex += 1) {
        const rule = this.rules[ruleIndex];
        if (!rule.tiers.length) return '每个规则维度至少保留一个充值层级';
        const tiers = rule.tiers.map(tier => ({ tier: tier, min: this.amountToCents(tier.min_recharge), max: tier.max_recharge === '' ? null : this.amountToCents(tier.max_recharge) }));
        if (tiers.some(item => !item.tier.tier_name || item.tier.tier_name.length > 10)) return '层级名称必填且最多10个字符';
        if (tiers.some(item => item.min === null || (item.tier.max_recharge !== '' && item.max === null))) return '充值金额必须是非负数且最多保留两位小数';
        tiers.sort((first, second) => first.min - second.min);
        if (tiers[0].min !== 0) return '充值层级必须从0开始';
        for (let tierIndex = 0; tierIndex < tiers.length; tierIndex += 1) {
          const item = tiers[tierIndex];
          if (tierIndex === tiers.length - 1) {
            if (item.max !== null) return '最后一个充值层级最高值必须留空';
          } else if (item.max === null || item.max !== tiers[tierIndex + 1].min || item.max <= item.min) {
            return '充值层级必须连续且不能重叠或留空档';
          }
          if (!item.tier.thresholds || item.tier.thresholds.length !== PERIODS.length * ACCOUNT_TYPES.length) return '每个充值层级必须分别配置单双号的四个工作时段';
          const keys = item.tier.thresholds.map(threshold => threshold.account_type + ':' + threshold.period_code);
          if (new Set(keys).size !== keys.length || ACCOUNT_TYPES.some(type => PERIODS.some(period => !keys.includes(type + ':' + period.period_code)))) return '单双号工作时段配置重复或不完整';
          for (let thresholdIndex = 0; thresholdIndex < item.tier.thresholds.length; thresholdIndex += 1) {
            const threshold = item.tier.thresholds[thresholdIndex];
            const values = [threshold.near_timeout_minutes, threshold.timeout_minutes, threshold.serious_timeout_minutes];
            if (values.some(value => !Number.isInteger(value) || value < 1 || value > 1440)) return '超时阈值必须是1至1440的整数分钟';
            if (!(values[0] < values[1] && values[1] < values[2])) return '每个时段必须满足：临近超时 < 超时 < 严重超时';
          }
        }
      }
      return '';
    },
    buildPayload() {
      return {
        status: this.status,
        rules: this.rules.map(rule => ({
          dimension: rule.dimension,
          main_games: rule.dimension === 'game' ? rule.main_game_app_keys.map(key => {
            const game = this.mainGameOptions.find(item => item.appKey === key);
            return { main_game_id: game ? game.id : null, main_game_app_key: key, main_game_name: game ? game.text : key };
          }) : [],
          tiers: rule.tiers.map(tier => ({
            tier_name: tier.tier_name,
            min_recharge: tier.min_recharge,
            max_recharge: tier.max_recharge,
            thresholds: tier.thresholds.map(threshold => ({
              account_type: threshold.account_type,
              period_code: threshold.period_code,
              near_timeout_minutes: threshold.near_timeout_minutes,
              timeout_minutes: threshold.timeout_minutes,
              serious_timeout_minutes: threshold.serious_timeout_minutes,
            })),
          })),
        })),
      };
    },
    async save() {
      if (this.loading || !this.configLoaded) return;
      const errorMessage = this.validateConfig();
      if (errorMessage) {
        this.$message.warning(errorMessage);
        return;
      }
      this.saving = true;
      try {
        const data = await saveQwAiQualityTimelinessRuleApi(this.buildPayload());
        if (!data || data.code !== 200) {
          this.$message.error((data && data.message) || '保存失败');
          return;
        }
        this.$message.success('时效质检规则已保存');
        this.dialogVisible = false;
      } catch (error) {
        this.$message.error('保存失败，请稍后重试');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.timeliness-dialog-body { min-height: 520px; }
.status-row { display: flex; align-items: center; justify-content: space-between; padding: 0 4px 16px; border-bottom: 1px solid #e8edf3; }
.sub-text { margin-top: 6px; color: #909399; font-size: 12px; }
.rule-filter { display: flex; align-items: center; gap: 12px; margin-top: 16px; }
.rule-filter .sub-text { margin-top: 0; }
.empty-rules { padding: 24px 8px; color: #909399; text-align: center; }
.rule-layout { display: flex; min-height: 455px; margin-top: 16px; border: 1px solid #e4e7ed; border-radius: 4px; overflow: hidden; }
.rule-sidebar { width: 230px; flex: 0 0 230px; padding: 14px; background: #f7f9fc; border-right: 1px solid #e4e7ed; }
.section-head, .tier-head, .tier-selector { display: flex; align-items: center; justify-content: space-between; }
.rule-list { max-height: 405px; margin-top: 10px; overflow-y: auto; }
.rule-item { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 8px; padding: 10px 12px; color: #606266; background: #fff; border: 1px solid #dcdfe6; border-radius: 4px; cursor: pointer; text-align: left; }
.rule-item.active { color: #409eff; border-color: #409eff; background: #ecf5ff; }
.rule-item-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rule-content { flex: 1; min-width: 0; padding: 18px; }
.dimension-row { padding-bottom: 4px; border-bottom: 1px solid #ebeef5; }
.tier-head { margin: 16px 0; }
.tier-selector { gap: 12px; }
.tier-form { padding: 16px; background: #fafafa; border: 1px solid #ebeef5; border-radius: 4px; }
.range-inputs { display: flex; align-items: center; gap: 10px; }
.range-inputs .el-input { width: 250px; }
.threshold-table { margin-top: 6px; }
.threshold-table /deep/ .el-input-number { width: 155px; }
.rule-hint { margin-top: 14px; padding: 10px 14px; color: #7d8795; line-height: 1.8; background: #f5f7fa; border-radius: 4px; }
</style>
